// ============================================================
// useSheetEditor - the live sheet's writes for one combatant.
//
// Both panels use it: the target side for whoever is targeted, the actor
// side for whoever you are acting as. A sheet edit goes through
// `editLiveSheet` (pins rebased, undamaged armour carried, a statblock
// folded first) and lands in ONE roster write, so no client ever sees
// the sheet moved and its pins not yet caught up.
// ============================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRosterContext } from '../contexts/RosterContext';
import type { Character } from '../engine/character/types';
import { listPins, type Pin, type PinRef } from '../engine/roster/pins';
import { isBodyChange, resetChange, sheetChanges, type SheetChange } from '../engine/roster/sheetChanges';
import { rebodiedState } from '../engine/roster/rebody';
import { isCrowd, resolveBodyShape } from '../engine/bodyShape';
import {
  editLiveSheet,
  readyForSheetEdit,
  releasePins,
  setPin,
  sheetEditFlash,
  type CombatantHalves,
} from '../engine/roster/sheetEdit';
import type { CondFlash } from '../engine/traitCleanup';
import type { Combatant, CombatState } from '../engine/roster/types';

export interface SheetEditor {
  /**
   * The combatant AS THE SHEET SHOWS IT: a statblock with its pins
   * already folded into its sheet. Draw every patch from this sheet -
   * it is the one the patch will land on.
   */
  combatant: Combatant | null;
  /** The values pinned by hand over the sheet. */
  pins: Pin[];
  /**
   * Lay a partial over the sheet, optionally changing the live state and
   * letting pins go in the same write. A no-op with nobody to edit.
   */
  editSheet: (patch: Partial<Character>, alsoState?: (state: CombatState) => CombatState, release?: PinRef[]) => void;
  /** Let one pin go, so the sheet's own answer shows again. */
  resetPin: (pin: Pin) => void;
  /** Pin a value over the sheet: what only the table can say. */
  setPin: (ref: PinRef, value: unknown) => void;
  /** "Pinned removed by From Beyond": what the last edit stripped, for three seconds. */
  flash: CondFlash | null;
  /** What the live sheet says differently from its library original; empty when it has none. */
  changes: SheetChange[];
  /** Whether there is a library original to compare against and reset to. */
  hasOriginal: boolean;
  /** Put one changed value back to the library's. */
  resetChange: (change: SheetChange) => void;
  /** Save the combatant back to the library (asks: the sheet, or wounds and all). */
  saveToLibrary: () => void;
}

/** The sheet's writes for one combatant id; each a no-op with nobody to edit. */
function sheetWrites(combatantId: string | null, editCombatant: EditCombatant) {
  const write = (change: (c: Combatant) => CombatantHalves) => {
    if (combatantId) editCombatant(combatantId, change);
  };
  return {
    editSheet: (patch: Partial<Character>, alsoState?: (state: CombatState) => CombatState, release?: PinRef[]) =>
      write((c) => editLiveSheet(c, patch, alsoState, release)),
    // As whole edits: the live state follows, and a returning immunity strips what it rules out.
    resetPin: (pin: Pin) => write((c) => releasePins(readyForSheetEdit(c), [pin])),
    setPin: (ref: PinRef, value: unknown) => write((c) => setPin(c, ref, value)),
  };
}

type EditCombatant = (id: string, change: (c: Combatant) => CombatantHalves) => void;

/**
 * @param combatantId who the sheet is of; null for nobody.
 * @param shown whether the sheet is on screen. The folded view and the
 *   pin list are only worked out while it is - both panels mount this
 *   hook on every render, whichever tab is open.
 */
export default function useSheetEditor(combatantId: string | null, shown: boolean): SheetEditor {
  const { editCombatant, onSaveCombatantToLibrary } = useRosterContext();
  const view = useSheetView(shown ? combatantId : null);
  const { combatant, original } = view;
  const writes = sheetWrites(combatantId, editCombatant);
  const [flash, setFlash] = useFlash();
  // The flash is read off the combatant as shown, BEFORE the write lands.
  const editSheet: SheetEditor['editSheet'] = (patch, alsoState, release) => {
    if (combatant) setFlash(sheetEditFlash(combatant, patch));
    writes.editSheet(patch, alsoState, release);
  };
  const save = () => {
    if (combatantId) onSaveCombatantToLibrary?.(combatantId);
  };
  return {
    combatant,
    pins: view.pins,
    changes: view.changes,
    hasOriginal: !!original,
    editSheet,
    resetPin: writes.resetPin,
    setPin: writes.setPin,
    flash,
    resetChange: (change) => resetToOriginal(combatant, original, change, editSheet),
    saveToLibrary: save,
  };
}

/** Put one changed value back to the library's; the body goes back whole, live state included. */
function resetToOriginal(
  combatant: Combatant | null,
  original: Character | null,
  change: SheetChange,
  editSheet: SheetEditor['editSheet'],
) {
  if (!combatant || !original) return;
  const patch = resetChange(combatant.sheet, original, change.path);
  editSheet(patch, isBodyChange(change.path) ? rebodiedAs(original) : undefined);
}

/** The live state put into the body the library sheet has. */
function rebodiedAs(original: Character) {
  const [shape, crowd] = [resolveBodyShape(original), isCrowd(original)];
  return (state: CombatState) => rebodiedState(state, shape, crowd, original.armour?.locations ?? {});
}

/** What the sheet shows of one combatant: the folded view, its pins, its library original and what differs. */
function useSheetView(combatantId: string | null) {
  const { roster, libraryCharacters } = useRosterContext();
  // Keyed on the combatant ITSELF, not the roster: the roster is a new array on
  // anyone's wound tick, and folding a statblock again each time would hand
  // every section a new sheet to re-render for.
  const live = combatantId ? (roster as Combatant[] | undefined)?.find((c) => c.id === combatantId) : null;
  const combatant: Combatant | null = useMemo(() => (live ? readyForSheetEdit(live) : null), [live]);
  const pins = useMemo(() => (combatant ? listPins(combatant) : []), [combatant]);
  const original = useOriginal(combatant, libraryCharacters);
  const changes = useMemo(() => (combatant ? sheetChanges(combatant.sheet, original) : []), [combatant, original]);
  return { combatant, pins, original, changes };
}

/**
 * The library sheet a combatant's own sheet came from: the entry with the
 * same character id. A plain statblock has none.
 */
function useOriginal(combatant: Combatant | null, library: Character[] | undefined): Character | null {
  const id = combatant?.kind === 'character' ? combatant.sheet.id : null;
  return useMemo(() => (id ? (library?.find((entry) => entry.id === id) ?? null) : null), [id, library]);
}

/** A message that clears itself after three seconds; setting null leaves a showing one alone. */
function useFlash(): [CondFlash | null, (flash: CondFlash | null) => void] {
  const [flash, setFlash] = useState<CondFlash | null>(null);
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(timer);
  }, [flash]);
  const show = useCallback((next: CondFlash | null) => {
    if (next) setFlash(next);
  }, []);
  return [flash, show];
}
